export type ScreenSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xl-wp' | 'xxl';
export type ImageSizes = Partial<Record<ScreenSize, number | string>>;

/**
 * Generates the image sizes string based on the current screen size for `<img sizes>` and `<link imagesizes>`. https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#sizes
 *
 * @param imageSizes An object with screen sizes as keys and image sizes as values.
 * Screen sizes are based on `$breakpoints` in `_variables.scss`.
 * ```ts
 * type ImageSizes = Record<ScreenSize, number | string>;
 * type ScreenSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xl-wp' | 'xxl';
 * ```
 * @constant screens The screen sizes in pixels.
 * ```ts
 * const screens = {
 *  xxs: 300,
 *  xs: 400,
 *  sm: 476,
 *  md: 768,
 *  lg: 1120,
 *  xl: 1140,
 *  'xl-wp': 1500,
 *  xxl: 1920,
 * };
 * ```
 * @example
 * ```ts
 * const imageSizes = useImageSizer({
 *  xs: '320px',
 *  sm: 450,
 *  xxl: '100vw',
 * });
 * ```
 * @returns The image sizes string in media query format.
 * eg.: `'(max-width: 400px) 320px, (max-width: 476px) 450px, 100vw'`.
 */
export const useImageSizer = (imageSizes: ImageSizes | string): ComputedRef<string> => {
  const screens = {
    xxs: 300,
    xs: 400,
    sm: 476,
    md: 768,
    lg: 1120,
    xl: 1140,
    'xl-wp': 1500,
    xxl: 1920,
  };

  return computed(() => {
    if (typeof imageSizes === 'string') {
      return imageSizes;
    }

    const querySizeString = Object.entries(imageSizes).reduce((acc, [screen, size], index, thisArr) => {
      const screenSize = screens[screen as ScreenSize];
      if (screenSize && size) {
        if (index === thisArr.length - 1) {
          if (screen === 'xxl') {
            acc.push(`${size}${typeof size === 'number' ? 'px' : ''}`);
          }
          acc.push('100vw');
        } else {
          acc.push(`(max-width: ${screenSize}px) ${size}${typeof size === 'number' ? 'px' : ''}`);
        }
      }
      return acc;
    }, [] as string[]);

    return querySizeString.join(', ');
  });
};
